import React from 'react';
import { Box, Text, Grid, HStack, Input, Icon, Flex } from '@chakra-ui/react';
import { Search, WandSparkles, WandSparklesIcon } from 'lucide-react';
import { PresetCard } from './PresetCard';
import { UserFeedPreference } from '../../../apis/user-preferences';
import NoDataComponent from '../../../components/NoData';
import PlusIcon from '../../../assets/plus-green-thin-icon.svg';
import LightBulb from '../../../assets/lightbulb.svg';
import Psychology from '../../../assets/psychology.svg';
import WandStars from '../../../assets/wand-stars.svg';
import Button from 'src/components/Button';

interface PresetSettingsProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  defaultPresets: { data?: UserFeedPreference[] };
  userPresets: { data?: UserFeedPreference[] };
  activePreset: { data?: UserFeedPreference };
  getPresetIcon: (preset: UserFeedPreference) => React.ReactElement;
  handlePresetSelect: (preset: UserFeedPreference) => void;
  handleViewSettings: (preset: UserFeedPreference, isCustom: boolean) => void;
  handleCreateNewMode: () => void;
  handleDeletePreset: (presetId: string) => void;
}

export const PresetSettings: React.FC<PresetSettingsProps> = ({
  searchQuery,
  onSearchChange,
  defaultPresets,
  userPresets,
  activePreset,
  getPresetIcon,
  handlePresetSelect,
  handleViewSettings,
  handleCreateNewMode,
  handleDeletePreset
}) => {
  return (
    <Box display="flex" flexDir="column" gap={5}>
      <div className="flex flex-col md:flex-row justify-between">
        <Box>
          <Text fontWeight="bold" fontSize="md" className="flex flex-row gap-1">
            Control the algorithm
            <img src={Psychology} alt="" className="w-4 h-4" />
          </Text>
          <Text fontSize="md">
            Customize your content feed to see more of what matters to <b>you</b>.
          </Text>
        </Box>
        <HStack spacing={4}>
          <Button
            className="flex flex-row items-center gap-2 w-full md:w-auto"
            variant="secondary"
            size="sm"
            h="36px"
            minW="90px"
            onClick={handleCreateNewMode}
          >
            <p>Create New Mode</p>
            <img src={PlusIcon} alt="" className="w-3 h-3" />
          </Button>
        </HStack>
      </div>

      <Box position="relative">
        <Input
          placeholder="Search Modes"
          pl={10}
          h={'43px'}
          fontSize={'14px'}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <Icon
          as={Search}
          position="absolute"
          left={3}
          top="50%"
          transform="translateY(-50%)"
          color="gray.400"
        />
      </Box>

      <Box>
        <Text fontWeight="bold" fontSize="md">
          Preset Modes
        </Text>
        <Text fontSize="sm">Modes we created for you</Text>
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={4}
          justifyContent="center"
          alignItems="start"
          maxW="1200px"
          mx="auto"
          mt={2}
        >
          {defaultPresets.data?.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              isCustom={false}
              isActive={activePreset.data?.id === preset.id}
              icon={getPresetIcon(preset)}
              onSelect={() => handlePresetSelect(preset)}
              handleViewSettings={handleViewSettings}
            />
          ))}
        </Grid>
      </Box>

      <Box>
        <Text fontWeight="bold" fontSize="md">
          Your Custom Modes
        </Text>
        <Text fontSize="sm" className="flex flex-row items-center gap-1">
          <img src={WandStars} alt="" className="w-3 h-3 hidden md:inline-block" />
          AI-powered feed customization, created by you.
        </Text>

        {userPresets.data?.length ? (
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
            justifyContent="center"
            alignItems="start"
            maxW="1200px"
            mx="auto"
            mt={2}
          >
            {userPresets.data?.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                isActive={activePreset.data?.id === preset.id}
                icon={getPresetIcon(preset)}
                onSelect={() => handlePresetSelect(preset)}
                isCustom={true}
                handleViewSettings={handleViewSettings}
                onDelete={() => handleDeletePreset(preset.id)}
              />
            ))}
          </Grid>
        ) : (
          <Box display="flex" justifyContent="center" mt={10}>
            <NoDataComponent
              title="It looks like you haven't created a custom mode yet."
              titleButton="Create New Mode"
              iconButton={PlusIcon}
              onClick={handleCreateNewMode}
              btnVariant="secondary"
              icon={LightBulb}
              outlinedIcon={true}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
